import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useStatusMetrics } from "@/hooks/useStatusMetrics";
import { DateRangePicker } from "@/components/shared/filters/DateRangePicker";
import { startOfMonth, endOfMonth } from "date-fns";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import {
  Clock,
  TrendingUp,
  AlertTriangle,
  Activity,
  Timer,
  BarChart3,
  AlertCircle,
  Info,
  CheckCircle,
} from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  useCollectsErrors,
  useCollectsDivergences,
  useMonitoringStats,
} from "@/hooks/useCollectsMonitoring";
import { CollectsMonitoringTable } from "@/components/status-metrics/CollectsMonitoringTable";

const STATUS_LABELS: Record<string, string> = {
  solicitacao_criada: "Solicitação Criada",
  aguardando_aprovacao: "Aguardando Aprovação",
  aguardando_agendamento: "Aguardando Agendamento",
  agendado: "Agendado",
  coletado: "Coletado",
  destinado: "Destinado",
  cancelado: "Cancelado",
  reprovado: "Reprovado",
};

const STATUS_COLORS: Record<string, string> = {
  solicitacao_criada: "#4A90E2",
  aguardando_aprovacao: "#F39C12",
  aguardando_agendamento: "#E67E22",
  agendado: "#9B59B6",
  coletado: "#27AE60",
  destinado: "#2D5A3D",
  cancelado: "#E74C3C",
  reprovado: "#C0392B",
};

export default function StatusMetrics() {
  const [dateRange, setDateRange] = useState<{ start: Date; end: Date }>({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  });

  const {
    data: metrics,
    isLoading,
    error,
  } = useStatusMetrics(dateRange.start, dateRange.end);

  // Monitoramento de coletas problemáticas
  const { data: errors, isLoading: errorsLoading } = useCollectsErrors();
  const { data: divergences, isLoading: divergencesLoading } =
    useCollectsDivergences();
  const monitoringStats = useMonitoringStats();

  const problems =
    divergences?.filter((d) => d.classification === "NEEDS_INVESTIGATION") ||
    [];
  const coalesceItems =
    divergences?.filter((d) => d.classification === "COALESCE_EXPECTED") || [];

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-[#10261F]"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-96 bg-white shadow-lg">
          <CardHeader>
            <CardTitle className="text-red-600">
              Erro ao carregar métricas
            </CardTitle>
            <CardDescription className="text-gray-600">
              {error.message}
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  const chartData =
    metrics?.map((m) => ({
      name: STATUS_LABELS[m.status] || m.status,
      status: m.status,
      hours: Number(m.avg_duration_hours.toFixed(1)),
      occurrences: m.total_occurrences,
    })) || [];

  const totalCollects =
    metrics?.reduce((sum, m) => sum + m.total_occurrences, 0) || 0;
  const avgDuration =
    metrics?.reduce(
      (sum, m) => sum + m.avg_duration_hours * m.total_occurrences,
      0,
    ) / totalCollects || 0;
  const bottlenecks =
    metrics
      ?.filter((m) => m.avg_duration_hours > 48)
      .sort((a, b) => b.avg_duration_hours - a.avg_duration_hours) || [];

  return (
    <div className="min-h-screen bg-background p-4 md:p-6">
      <div className="max-w-7xl mx-auto space-y-4 md:space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#10261F] ">
              Dashboard de Métricas de Status
            </h1>
            <p className="text-gray-600 mt-2  text-sm md:text-base">
              Análise detalhada do tempo em cada status
            </p>
          </div>
          <DateRangePicker
            value={dateRange}
            onChange={setDateRange}
            className="md:w-auto"
          />
        </div>

        {/* KPIs */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-600 ">
                    Total de Coletas
                  </p>
                  <p className="text-2xl font-bold text-[#10261F] ">
                    {totalCollects}
                  </p>
                  <p className="text-xs text-gray-500 ">
                    No período selecionado
                  </p>
                </div>
                <div className="p-2.5 bg-[#2D5A3D]/10 rounded-lg">
                  <Activity className="h-5 w-5 text-[#2D5A3D]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-600 ">
                    Tempo Médio Total
                  </p>
                  <p className="text-2xl font-bold text-[#10261F] ">
                    {avgDuration.toFixed(1)}h
                  </p>
                  <p className="text-xs text-gray-500 ">
                    Da criação até destinação
                  </p>
                </div>
                <div className="p-2.5 bg-blue-50 rounded-lg">
                  <Timer className="h-5 w-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div className="space-y-1">
                  <p className="text-sm font-medium text-gray-600 ">
                    Gargalos Identificados
                  </p>
                  <p className="text-2xl font-bold text-[#10261F] ">
                    {bottlenecks.length}
                  </p>
                  <p className="text-xs text-gray-500 ">
                    Status com +48h de média
                  </p>
                </div>
                <div className="p-2.5 bg-red-50 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Gráfico de Duração Média */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-4">
            <div className="flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-[#2D5A3D]" />
              <CardTitle className="text-[#10261F] ">
                Tempo Médio por Status
              </CardTitle>
            </div>
            <CardDescription className="text-gray-600 ">
              Duração média (em horas) que as coletas permanecem em cada status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div style={{ width: "100%", height: "400px" }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                >
                  <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                  <XAxis
                    dataKey="name"
                    angle={-45}
                    textAnchor="end"
                    height={120}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                  />
                  <YAxis
                    label={{
                      value: "Horas",
                      angle: -90,
                      position: "insideLeft",
                      style: { fill: "#6B7280" },
                    }}
                    tick={{ fontSize: 12, fill: "#6B7280" }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#ffffff",
                      border: "1px solid #e0e0e0",
                      borderRadius: "8px",
                      boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    }}
                    formatter={(value: number, name: string, props: any) => [
                      `${value}h (${props.payload.occurrences} coletas)`,
                      "Tempo Médio",
                    ]}
                    labelStyle={{ color: "#10261F", fontWeight: 600 }}
                  />
                  <Bar dataKey="hours" radius={[8, 8, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={STATUS_COLORS[entry.status] || "#94A3B8"}
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>

        {/* Tabela de Detalhes */}
        <Card className="bg-white shadow-sm">
          <CardHeader className="pb-4">
            <CardTitle className="text-[#10261F] ">
              Detalhamento por Status
            </CardTitle>
            <CardDescription className="text-gray-600 ">
              Estatísticas completas de duração em cada status
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200">
                    <th className="text-left py-3 px-4 font-semibold text-gray-700  text-sm">
                      Status
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700  text-sm">
                      Ocorrências
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700  text-sm">
                      Média
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700  text-sm">
                      Mínimo
                    </th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-700  text-sm">
                      Máximo
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {metrics?.map((metric) => (
                    <tr
                      key={metric.status}
                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 font-medium text-[#10261F]">
                        <div className="flex items-center gap-2">
                          <div
                            className="w-3 h-3 rounded-full"
                            style={{
                              backgroundColor: STATUS_COLORS[metric.status],
                            }}
                          />
                          <span className="">
                            {STATUS_LABELS[metric.status] || metric.status}
                          </span>
                        </div>
                      </td>
                      <td className="text-right py-3 px-4 text-gray-700 ">
                        {metric.total_occurrences}
                      </td>
                      <td className="text-right py-3 px-4 font-semibold text-[#10261F] ">
                        {metric.avg_duration_hours.toFixed(1)}h
                      </td>
                      <td className="text-right py-3 px-4 text-gray-600 ">
                        {metric.min_duration_hours.toFixed(1)}h
                      </td>
                      <td className="text-right py-3 px-4 text-gray-600 ">
                        {metric.max_duration_hours.toFixed(1)}h
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Alertas de Gargalos */}
        {bottlenecks.length > 0 && (
          <Card className="bg-white shadow-sm border-l-4 border-l-red-500">
            <CardHeader className="pb-4">
              <CardTitle className="flex items-center gap-2 text-red-600 ">
                <AlertTriangle className="h-5 w-5" />
                Gargalos Identificados
              </CardTitle>
              <CardDescription className="text-gray-600 ">
                Status com tempo médio superior a 48 horas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {bottlenecks.map((bottleneck) => (
                  <div
                    key={bottleneck.status}
                    className="flex items-center justify-between p-4 rounded-lg bg-red-50 border border-red-100"
                  >
                    <div>
                      <div className="font-semibold text-[#10261F] ">
                        {STATUS_LABELS[bottleneck.status] || bottleneck.status}
                      </div>
                      <div className="text-sm text-gray-600 ">
                        {bottleneck.total_occurrences} coletas afetadas
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xl font-bold text-red-600 ">
                        {bottleneck.avg_duration_hours.toFixed(1)}h
                      </div>
                      <div className="text-xs text-gray-600 ">
                        tempo médio
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Separador */}
        <div className="border-t-2 border-gray-300 my-8"></div>

        {/* Seção de Monitoramento de Processamento */}
        <div className="space-y-4">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-[#10261F] ">
              Monitoramento de Processamento
            </h2>
            <p className="text-gray-600 mt-1  text-sm">
              Análise de erros e divergências na sincronização de dados
            </p>
          </div>

          {/* KPIs de Monitoramento */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 md:gap-4">
            <Card className="bg-white border shadow-sm border-l-4 border-l-red-500">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-600 ">
                      Erros de Processamento
                    </p>
                    <p className="text-2xl font-bold text-red-600 ">
                      {monitoringStats.total_errors}
                    </p>
                    <p className="text-xs text-gray-500 ">
                      Última semana
                    </p>
                  </div>
                  <div className="p-2.5 bg-red-50 rounded-lg">
                    <AlertCircle className="h-5 w-5 text-red-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border shadow-sm border-l-4 border-l-amber-500">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-600 ">
                      Divergências Problemáticas
                    </p>
                    <p className="text-2xl font-bold text-amber-600 ">
                      {monitoringStats.total_problems}
                    </p>
                    <p className="text-xs text-gray-500 ">
                      Requer investigação
                    </p>
                  </div>
                  <div className="p-2.5 bg-amber-50 rounded-lg">
                    <AlertTriangle className="h-5 w-5 text-amber-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-white border shadow-sm border-l-4 border-l-blue-500">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-600 ">
                      Divergências COALESCE
                    </p>
                    <p className="text-2xl font-bold text-blue-600 ">
                      {monitoringStats.total_coalesce}
                    </p>
                    <p className="text-xs text-gray-500 ">
                      Comportamento esperado
                    </p>
                  </div>
                  <div className="p-2.5 bg-blue-50 rounded-lg">
                    <Info className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Tabs com tabelas */}
          <Card className="bg-white shadow-sm">
            <CardHeader className="pb-4">
              <CardTitle className="text-[#10261F] ">
                Detalhamento de Coletas
              </CardTitle>
              <CardDescription className="text-gray-600 ">
                Visualize erros de processamento e divergências detectadas
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs defaultValue="errors" className="w-full">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger
                    value="errors"
                    className="flex items-center gap-2"
                  >
                    <AlertCircle className="h-4 w-4" />
                    Erros ({monitoringStats.total_errors})
                  </TabsTrigger>
                  <TabsTrigger
                    value="problems"
                    className="flex items-center gap-2"
                  >
                    <AlertTriangle className="h-4 w-4" />
                    Problemas ({monitoringStats.total_problems})
                  </TabsTrigger>
                  <TabsTrigger
                    value="coalesce"
                    className="flex items-center gap-2"
                  >
                    <CheckCircle className="h-4 w-4" />
                    COALESCE ({monitoringStats.total_coalesce})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="errors" className="mt-4">
                  {errorsLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10261F]"></div>
                    </div>
                  ) : (
                    <CollectsMonitoringTable
                      type="errors"
                      data={errors || []}
                    />
                  )}
                </TabsContent>

                <TabsContent value="problems" className="mt-4">
                  {divergencesLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10261F]"></div>
                    </div>
                  ) : (
                    <CollectsMonitoringTable type="problems" data={problems} />
                  )}
                </TabsContent>

                <TabsContent value="coalesce" className="mt-4">
                  {divergencesLoading ? (
                    <div className="flex justify-center py-12">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#10261F]"></div>
                    </div>
                  ) : (
                    <CollectsMonitoringTable
                      type="coalesce"
                      data={coalesceItems}
                    />
                  )}
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
